import React, {
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';

import { useRouter } from 'next/router';
import PropTypes from 'prop-types';
import styled, { css } from 'styled-components';

import VisuallyHidden from '~/shared/components/VisuallyHidden';
import { Link } from '~/techstyle-shared/next-routes';
import { useId } from '~/techstyle-shared/react-components';
import { FormattedMessage } from '~/techstyle-shared/react-intl';

const FlyOutMenuContext = React.createContext();

const FlyOutMenuInner = styled.div`
  display: flex;
  flex-direction: row;
  align-items: center;
`;

/**
 * The parent to a popup menu, its visibility toggle button and optional link,
 * coordinating the interactions between them
 *
 * Basic FlyOutMenu usage:
 * <FlyOutMenu.Parent>
 *   <FlyOutMenu.Toggle>Account</FlyOutMenu.Toggle>
 *   <FlyOutMenu.Popup>
 *     <ul>
 *       <li>
 *         <a href="/billing">Billing</a>
 *       </li>
 *       <li>
 *         <a href="/signout">Sign Out</a>
 *       </li>
 *     </ul>
 *   </FlyOutMenu.Popup>
 * </FlyOutMenu.Parent>
 *
 * Usage with a link:
 * <FlyOutMenu.Parent>
 *   <FlyOutMenu.Link href="/account">My Account</FlyOutMenu.Link>
 *   <FlyOutMenu.Toggle
 *   // Children here are needed in order to give this the proper accessible name.
 *   // Use the same text used inside the link
 *   >
 *     My Account
 *   </FlyOutMenu.Toggle>
 *   <FlyOutMenu.Popup>
 *     <ul>
 *       <li>
 *         <a href="/billing">Billing</a>
 *       </li>
 *       <li>
 *         <a href="/signout">Sign Out</a>
 *       </li>
 *     </ul>
 *   </FlyOutMenu.Popup>
 * </FlyOutMenu.Parent>
 *
 */
const FlyOutMenuParent = ({
  children,
  className,
  setChildPopupState = () => {},
  onBlur,
  ...otherProps
}) => {
  const expandedByExternalToggle = useRef(otherProps.isOn ?? false);
  const [expanded, setExpandedState] = useState(
    expandedByExternalToggle.current
  );
  const ref = useRef(null);
  const linkIdRef = useRef(undefined);
  const navContentId = useId('flyout-parent-');

  const noDelay = otherProps.noDelay ?? false;

  const defaultDelay = 500; // in milliseconds
  const delay = otherProps.delay ?? defaultDelay;
  const timer = useRef(null);

  const [touched, setTouched] = useState(false);

  // optionally allow a delay before setting expanded state
  const setExpanded = useCallback(
    (value, timeout = 0) => {
      timer.current = setTimeout(() => {
        setExpandedState(value);
        setChildPopupState(false);
      }, timeout);
    },
    [setChildPopupState]
  );

  const contextValue = useMemo(
    () => ({
      expanded,
      setExpanded,
      navContentId,
      parentRef: ref,
      linkIdRef,
      touched,
      setTouched,
    }),
    [expanded, setExpanded, navContentId, linkIdRef, touched, setTouched, ref]
  );

  useEffect(() => {
    if (Object.hasOwn(otherProps, 'isOn')) {
      if (expandedByExternalToggle.current !== otherProps.isOn) {
        expandedByExternalToggle.current = otherProps.isOn;
        setExpanded(otherProps.isOn);
      }
    }
  }, [otherProps, expandedByExternalToggle, setExpanded]);

  return (
    <FlyOutMenuContext.Provider value={contextValue}>
      <FlyOutMenuInner
        {...otherProps}
        onMouseEnter={() => {
          if (expanded) {
            clearTimeout(timer.current);
          }
        }}
        onMouseLeave={() => {
          if (expanded && !expandedByExternalToggle.current && !touched) {
            setExpanded(false, noDelay ? 0 : delay);
          }
        }}
        ref={ref}
        onBlur={(event) => {
          // If a blur event happens anywhere within the popup toggle button or
          // the contents of the popup, we check to see if the next focused
          // element is still within the descendants of this element. If it
          // isn't, we collapse the popup.
          if (expanded && !ref.current.contains(event.relatedTarget)) {
            setExpanded(false);
          }
        }}
        onKeyDown={(event) => {
          let handled = true;

          switch (event.key) {
            case 'Escape':
              // The esc key collapses the popup when pressed inside
              if (expanded) {
                // Set focus back to the element that toggles the menu
                ref.current
                  .querySelector(':scope > a, :scope > button')
                  .focus();

                setExpanded(false);
              } else {
                handled = false;
              }
              break;

            default:
              handled = false;
          }

          if (handled) {
            event.preventDefault();
          }
        }}
      >
        {children}
      </FlyOutMenuInner>
    </FlyOutMenuContext.Provider>
  );
};
FlyOutMenuParent.propTypes = {
  children: PropTypes.node.isRequired,
  className: PropTypes.string,
  onBlur: PropTypes.func,
  setChildPopupState: PropTypes.func,
};

const UnstyledFlyOutMenuLink = React.forwardRef(function UnstyledFlyOutMenuLink(
  { href, children, ...otherProps },
  forwardedRef
) {
  const { linkIdRef, setExpanded } = useContext(FlyOutMenuContext);
  linkIdRef.current = useId('flyout-link-');

  return (
    <div onMouseEnter={() => setExpanded(true)}>
      <Link href={href}>
        <a {...otherProps} ref={forwardedRef} id={linkIdRef.current}>
          {children}
        </a>
      </Link>
    </div>
  );
});
UnstyledFlyOutMenuLink.propTypes = {
  children: PropTypes.node.isRequired,
  href: PropTypes.string.isRequired,
  isTouchEnabled: PropTypes.bool,
};

const excludeFocusedStyleConfig = {
  shouldForwardProp: (prop) => !['focusedStyle'].includes(prop),
};
const interactiveElementStyle = css`
  display: flex;
  align-items: center;
  font-weight: 600;
  height: 100%;
  cursor: pointer;
  position: relative;

  font-size: ${({ theme }) => theme.fontSizes.sm}px;

  ${({ theme }) => theme.builderHeader.flyOutMenu.interactiveElement};

  @supports not (selector(:focus-visible)) {
    &:focus {
      box-shadow: inset 0 -2px 0 0 black;
      ${({ focusedStyle }) => focusedStyle};
    }
  }

  &:focus-visible {
    box-shadow: inset 0 -2px 0 0 black;
    ${({ focusedStyle }) => focusedStyle};
  }

  /*
   * Double && here are to ensure the custom focus styles defined here don't
   * accidentally get applied to other instances of elements using these styles.
   * https://codesandbox.io/s/styled-components-selector-custom-props-gotcha-1gviu
   * https://styled-components.com/docs/basics#pseudoelements-pseudoselectors-and-nesting
   */
  *:hover > &&,
  *:focus-within > && {
    box-shadow: inset 0 -2px 0 0 black;
    ${({ focusedStyle }) => focusedStyle};
  }
`;

const FlyOutMenuLink = styled(UnstyledFlyOutMenuLink).withConfig(
  excludeFocusedStyleConfig
)`
  font: inherit;
  color: inherit;
  background: none;
  border: none;
  border-radius: 0;

  ${interactiveElementStyle};
`;
FlyOutMenuLink.propTypes = {
  /**
   * Styles to pass to the anchor when it is hovered, focused or expanded.
   */
  focusedStyle: PropTypes.any,
  isTouchEnabled: PropTypes.bool,
};

const UnstyledFlyOutMenuToggle = React.forwardRef(
  function UnstyledFlyOutMenuToggle(
    {
      children,
      onClick,
      onFocus,
      onKeyDown,
      isTouchEnabled = false,
      ...otherProps
    },
    forwardedRef
  ) {
    const {
      expanded,
      setExpanded,
      navContentId,
      linkIdRef,
      touched,
      setTouched,
      parentRef,
    } = useContext(FlyOutMenuContext);
    const isAccompaniedByLink = Boolean(linkIdRef.current);
    const router = useRouter();

    const closeMenu = () => setExpanded(false);

    useEffect(() => {
      if (router) {
        router.events.on('routeChangeComplete', closeMenu);
      }
      return () => {
        if (router) {
          router.events.off('routeChangeComplete', closeMenu);
        }
      };
      // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [router?.events, closeMenu]);

    return (
      <button
        type="button"
        {...otherProps}
        ref={forwardedRef}
        onMouseEnter={() => {
          if (!touched) {
            setExpanded(true);
          }
        }}
        onTouchStart={() => {
          parentRef.current.focus();
          if (isTouchEnabled) {
            setTouched(true);
          }
        }}
        onClick={(event) => {
          if (onClick && !touched) {
            onClick(event);
          }

          setExpanded((x) => !x);

          if (touched) {
            setTouched(false);
          }
        }}
        onFocus={(event) => {
          if (onFocus) {
            onFocus(event);
          }

          // If the corresponding popup menu is expanded when focus returns to
          // this toggle, collapse it (e.g., when tabbing back out of the
          // contents of the popup).
          if (expanded) {
            setExpanded(false);
          }
        }}
        onKeyDown={(event) => {
          if (onKeyDown) {
            onKeyDown(event);
          }
          // Let custom key handlers override the behavior of this handler
          if (event.defaultPrevented) {
            return;
          }

          let handled = true;

          switch (event.key) {
            case 'ArrowDown':
              if (!expanded) {
                setExpanded(true);
              }
              break;
            case 'ArrowUp':
              if (expanded) {
                setExpanded(false);
              }
              break;

            default:
              handled = false;
          }

          if (handled) {
            event.preventDefault();
          }
        }}
        aria-haspopup
        aria-expanded={expanded}
        aria-controls={navContentId}
      >
        {isAccompaniedByLink ? (
          <>
            <VisuallyHidden>
              <FormattedMessage
                defaultMessage="Toggle submenu for {linkName}"
                id="site_navigation.fly_out_menu_toggle_hint"
                values={{ linkName: children }}
              />
            </VisuallyHidden>
            &nbsp;
          </>
        ) : (
          children
        )}
      </button>
    );
  }
);
UnstyledFlyOutMenuToggle.propTypes = {
  children: PropTypes.node.isRequired,
  isTouchEnabled: PropTypes.bool,
  onClick: PropTypes.func,
  onFocus: PropTypes.func,
  onKeyDown: PropTypes.func,
};

const revealHiddenToggleStyle = css`
  width: auto;
  clip: auto;
  margin: 0;
`;
const FlyOutMenuToggle = styled(UnstyledFlyOutMenuToggle).withConfig(
  excludeFocusedStyleConfig
)`
  font-family: ${({ theme }) => theme.fontStacks.baseFamily};
  color: inherit;
  background: none;
  border: none;
  border-radius: 0;

  ${interactiveElementStyle};

  &::after {
    content: '';
    width: 0;
    height: 0;
    border-color: currentcolor transparent;
    border-style: solid;
    border-width: 9px 5px 0;
    position: absolute;
    top: 50%;
    right: ${({ theme }) => theme.sizes.sm - 2.5}px;
    transition: transform 200ms;
    transform: translateY(-50%);
    display: none;
  }

  @supports not (selector(:focus-visible)) {
    &:focus {
      &::after {
        display: block;
      }
    }
  }

  &:focus-visible {
    &::after {
      display: block;
    }
  }

  &[aria-expanded='true'] {
    box-shadow: inset 0 -2px 0 0 black;

    &::after {
      /* Arrow Icon is displayed in old nav when trying to utilize this component instead of flyt-component package (pls refer to FLCX-3403). */
      /* Issue occured only when FLCX-3384 fixes is applied and is visible only in old nav with this component. -Noriel F. */

      display: ${({ isTouchEnabled }) => (isTouchEnabled ? 'none' : 'block')};
      transform: translateY(-50%) rotateX(180deg);
    }
    ${({ focusedStyle }) => focusedStyle};
  }

  [href] + & {
    width: 1px;
    clip: rect(0 0 0 0);
    padding: 0;
    margin-left: -1px;

    overflow: hidden;
    &::after {
      display: block;
    }
  }

  @supports not (selector(:focus-visible)) {
    [href]:focus + &,
    [href] + &:focus {
      ${revealHiddenToggleStyle};
    }
  }
  [href]:focus-visible + &,
  [href] + &:focus-visible {
    ${revealHiddenToggleStyle};
  }
  [href] + &[aria-expanded='true'] {
    ${revealHiddenToggleStyle};
  }
`;

FlyOutMenuToggle.propTypes = {
  /**
   * Styles to pass to the button when it is hovered, focused or expanded.
   */
  focusedStyle: PropTypes.any,
  isTouchEnabled: PropTypes.bool,
};

const FlyOutMenuPopupContainer = styled.div`
  position: absolute;
  display: none;
  top: 100%;
  font-family: ${({ theme }) => theme.fontStacks.baseFamily};

  *[aria-expanded='true'] ~ & {
    display: block;
  }

  /* Create an invisible hover bridge above the menu */
  &::before {
    content: '';
    position: absolute;
    top: -20px;
    left: -30px;
    right: 0;
    height: 30px;
    background: transparent;
    pointer-events: auto;
  }
`;

/**
 * A popup menu that contains everything shown when the toggle button is activated
 */
const FlyOutMenuPopup = (props) => {
  const { navContentId } = useContext(FlyOutMenuContext);

  return <FlyOutMenuPopupContainer {...props} id={navContentId} />;
};

/**
 * Popup for use in the main site navigation, provided for convenience
 */
const FlyOutMenuPopupFullWidth = styled(FlyOutMenuPopup)`
  left: 0;
  right: 0;
`;

/**
 * Nav component for use in the main site navigation, provided for convenience
 */
const FlyOutMenuNav = styled.nav`
  height: 100%;
  display: flex;

  & > ul,
  & > ol {
    display: flex;
  }
`;

/**
 * Nav item component for use in the main site navigation, provided for convenience
 */
const FlyOutMenuListItem = styled(FlyOutMenuParent).attrs({
  forwardedAs: 'li',
})`
  list-style: none;
  font-family: ${({ theme }) => theme.fontStacks.baseFamily};
`;

// commented exports and components are legacy code from flyt-components repo that may no longer be needed
export default {
  Parent: FlyOutMenuParent,
  Toggle: FlyOutMenuToggle,
  UnstyledToggle: UnstyledFlyOutMenuToggle,
  Popup: FlyOutMenuPopup,
  // Link: FlyOutMenuLink,
  UnstyledLink: UnstyledFlyOutMenuLink,
  FLYT_DEFAULTS: {
    FlyOutMenuPopupFullWidth,
    FlyOutMenuNav,
    FlyOutMenuListItem,
  },
};
